import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { reducedMotion } from '../../hooks/useLenis.js';
import { submitForm } from '../../lib/submitForm.js';
import './Footer.css';

export const Footer = ({ showCta }) => {
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const statusRef = useRef(null);
  const currentYear = new Date().getFullYear();
  const location = useLocation();

  const isContactPage =
    location.pathname === '/contact' ||
    location.pathname === '/pages/contact.html' ||
    location.pathname.startsWith('/contact') ||
    location.pathname.startsWith('/pages/contact');

  const shouldRenderCta = showCta !== undefined ? showCta : !isContactPage;

  const handleNewsletterSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    try {
      await submitForm('/api/newsletter', form);
      form.reset();
      setNewsletterStatus('Thank you for subscribing.');
    } catch (err) {
      setNewsletterStatus(err.message);
    }

    if (!reducedMotion() && statusRef.current) {
      gsap.fromTo(
        statusRef.current,
        { opacity: 0, y: -6 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
      );
    }
  };

  return (
    <>
      {shouldRenderCta && (
        <section className="cta stage">
        {/* Night skyline in a navy duotone (desaturated + tinted in CSS), so the
            photo's bright fountains don't fight the brand colours. Static: the
            ASCII animation it replaces was the costliest effect on every page. */}
        <div className="cta-photo" aria-hidden="true">
          <img src="/media/city-lights.webp" alt="" loading="lazy" decoding="async" />
        </div>
        <img
          className="cta-mark"
          src="/media/logo_background_remove.png"
          alt=""
          aria-hidden="true"
          loading="lazy"
        />
        <div className="cta-overlay" aria-hidden="true" />
        <div className="shell">
          <div className="cta-content">
            <h2 data-reveal="chars">
              Strategies for Business Model Growth and Expansion
            </h2>
            <p>
              Our approach to growth rests on innovation, market
              diversification, client engagement and operational efficiency.
            </p>
            <a className="btn" href="/contact">
              Contact now
            </a>
          </div>
        </div>
      </section>
    )}

      <footer className="footer">
        <div className="footer-inner">
          <div>
            <div className="footer-logo-wrap">
              <img
                src="/media/logo_white.png"
                alt="ASTRID FIRST Wealth Advisory"
                className="footer-logo"
              />
            </div>
            <p className="footer-licence">
              Astrid First Wealth Advisory Pte Ltd holds a Capital Market Services
              (CMS) license issued by the Monetary Authority of Singapore (MAS)
              to conduct regulated activities in Fund Management under the
              Singapore Securities & Futures Act and Financial Advisors Act.
            </p>
          </div>

          <div>
            <h2>Quick Links</h2>
            <ul>
              <li>
                <a href="/introduction">Introduction</a>
              </li>
              <li>
                <a href="/business-model">Our Business Model</a>
              </li>
              <li>
                <a href="/faq">Frequently Asked Questions</a>
              </li>
              <li>
                <a href="/services">Our Services</a>
              </li>
              <li>
                <a href="/approach">Investment Approach</a>
              </li>
            </ul>
          </div>

          <div className="footer-contact">
            <h2>Contact us</h2>
            <address>
              <a href="tel:+6569217700">(+65) 6921 7700</a>
              <a href="mailto:info@astridfirst.com">
                info@astridfirst.com
              </a>
              <span>
                1 Temasek Avenue, #20-01, Millenia Tower, Singapore 039192
              </span>
            </address>
          </div>

          <div>
            <h2>Newsletter</h2>
            <p style={{ margin: 0 }}>
              Subscribe to our newsletter to get the latest updates.
            </p>
            <form
              className="newsletter"
              data-form
              onSubmit={handleNewsletterSubmit}
            >
              <input className="skip" type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <label className="skip" htmlFor="news-email">
                Email address
              </label>
              <input
                id="news-email"
                type="email"
                name="email"
                placeholder="Your email"
                required
              />
              <button type="submit">Send</button>
            </form>
            <p
              ref={statusRef}
              data-form-status
              role="status"
              style={{ marginTop: '0.8rem', fontSize: '0.8rem' }}
            >
              {newsletterStatus}
            </p>

            <div className="footer-social" aria-label="Social media channels">
              <a
                href="https://www.facebook.com"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <span className="skip">Facebook</span>
              </a>
              <a
                href="https://twitter.com/"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (formerly Twitter)"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
                <span className="skip">X (Twitter)</span>
              </a>
              <a
                href="https://www.instagram.com"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
                <span className="skip">Instagram</span>
              </a>
              <a
                href="https://linkedin.com/"
                className="footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
                <span className="skip">LinkedIn</span>
              </a>
            </div>
          </div>
        </div>

        <div className="footer-base">
          <span>
            Copyright © 2009–<span data-year>{currentYear}</span> by Astrid First
            Wealth Advisory. All Rights Reserved.
          </span>
          <a href="https://eservices.mas.gov.sg/fid/institution/detail/2241-ASTRID-FIRST-WEALTH-ADVISORY-PTE-LTD">
            MAS register
          </a>
        </div>
      </footer>
    </>
  );
};